import React from 'react';

import PropTypes from 'prop-types';
import { MdClose } from 'react-icons/md';
import styled from 'styled-components';

import {
  cioFacetsToUrlFilters,
  urlFiltersToCioFacets,
} from '~/grid/utils/cioUtils';
import useGridInputs, { GRID_TYPES } from '~/gridv1_1/hooks/useGridInputs';
import { sizeByKey } from '~/shared/utils/sizeConstants';
import useGender from '~/shared/utils/useGender';
import { FormattedMessage } from '~/techstyle-shared/react-intl';
import { useDomain } from '~/techstyle-shared/redux-core';

import { CUSTOM_COLOR_FILTER } from '../../constants.js';
import useSelectedFacetsToApply from '../../hooks/useSelectedFacetsToApply';
import useUnifiedProductGridData from '../../hooks/useUnifiedProductGridData';
import {
  getSelectedFacetRbid,
  getRbidByFacetNameAndFacetOption,
  getTransformedFacetOptionDisplayName,
  isCioSizeFacet,
} from '../../utils/filters';

const DynamicFormattedMessage = FormattedMessage;

const MobileGridSelectedFiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem 1.5rem;
  color: ${({ theme }) => theme.colors.gridSelectedFiltersTextColor};
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const NoFiltersHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0px 0 16px 0;
`;

const HeaderText = styled.p`
  font-size: 1rem;
  ${({ theme }) => theme.gridFilterStyles.headerStyle};
`;

const ClearButton = styled.button`
  font-size: 14px;
  line-height: 18px;
  font-weight: 400;
  text-decoration: underline;
  font-family: inherit;
  border: none;
  background: none;
  cursor: pointer;
  ${({ theme }) => theme.gridFilterStyles.clearAllButtonStyle};
`;

const FiltersList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin: 1rem 0 0 0;
  list-style-type: none;
`;

const FilterItem = styled.li`
  line-height: 16px;
  padding: 4px 8px;
  font-size: 0.75rem;
  white-space: pre-wrap;
  ${({ theme }) => theme.gridFilterStyles.tagStyle};
`;

const RemoveButton = styled.button`
  border: none;
  color: ${({ theme }) => theme.colors.gridSelectedFiltersTextColor};
  cursor: pointer;
  background: none;
  font-family: inherit;
  vertical-align: middle;
  ${({ theme }) => theme.typography.variants.gridEmphasized.textStyle};
`;

const Icon = styled(MdClose)`
  stroke-width: 2;
  margin-top: 2px;
  color: ${({ theme }) => theme.colors.gridSelectedFiltersPillIconColor};
`;

const ColorBox = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: none;
  position: relative;
  top: 3px;
  margin-right: 4px;
  background-color: ${(props) => props.color};
  display: inline-block;
`;

function ColorWheelFilterItem({ color }) {
  return (
    <>
      <ColorBox color={`rgb(${color.r}, ${color.g}, ${color.b})`} />
      <FormattedMessage
        defaultMessage="Custom Color"
        id="site_product_grid.custom_color_filter_label"
      />
    </>
  );
}

ColorWheelFilterItem.propTypes = {
  color: PropTypes.shape({
    r: PropTypes.number.isRequired,
    g: PropTypes.number.isRequired,
    b: PropTypes.number.isRequired,
  }).isRequired,
};

const MobileGridSelectedFilters = ({ closeAllPanels }) => {
  const { allRegisteredFacets, customFacetFilters } =
    useUnifiedProductGridData();
  const domain = useDomain();
  const { gender } = useGender();
  const { gridType } = useGridInputs();

  const {
    clearAllSelectedFacetsToApply,
    selectedFacetsToApply,
    setSelectedFacetsToApply,
    toggleSelectedFacetsToApply,
  } = useSelectedFacetsToApply();

  const selectedFacets = Object.entries(selectedFacetsToApply).flatMap(
    ([key, value]) => {
      // Handle custom_color specially (not in allRegisteredFacets)
      if (key === CUSTOM_COLOR_FILTER.name) {
        return value?.map((option) => ({
          facetName: key,
          urlName: key,
          facetOption: option,
          facetOptionDisplayName: CUSTOM_COLOR_FILTER.displayName,
        }));
      }

      const facetName = urlFiltersToCioFacets[key] || key;
      const facet = allRegisteredFacets?.find(
        (facet) =>
          facet?.name ===
          (gridType === GRID_TYPES.PRODUCTS_V2_GRID ? key : facetName)
      );

      if (facet) {
        return value?.map((option) => {
          const translatedName = sizeByKey({
            domain,
            gender,
            category: facetName,
            key: option,
          });
          const cioOption = facet?.options?.find(
            (facetOption) => String(facetOption?.value) === String(option)
          );

          return {
            facetName,
            urlName: key,
            facetOption: translatedName || option,
            facetOptionDisplayName: getTransformedFacetOptionDisplayName({
              selectedFacetName: facetName,
              selectedFacetDisplayName: facet?.display_name,
              selectedFacetOptionDisplayName: cioOption?.display_name,
            }),
          };
        });
      }
      return [];
    }
  );

  const selectedFacetNames = new Set(Object.keys(selectedFacetsToApply));
  const validCustomFacetFilters = Object.values(customFacetFilters).filter(
    (item) => !item.hasError && !selectedFacetNames.has(item.name)
  );

  const handleCustomFilterRemove = (customFilter) => {
    setSelectedFacetsToApply((prev) => ({
      ...prev,
      [customFilter.name]: [],
    }));
  };

  const handleClearAllFilters = () => {
    clearAllSelectedFacetsToApply(
      validCustomFacetFilters.map(({ name }) => name)
    );
    closeAllPanels();
  };

  const handleOnClick = ({ facetName, facetOption }) => {
    toggleSelectedFacetsToApply({
      facetName,
      facetOption,
    });
  };

  return (
    <MobileGridSelectedFiltersContainer>
      {selectedFacets?.length === 0 &&
      !Object.keys(validCustomFacetFilters).length ? (
        <NoFiltersHeaderRow>
          <HeaderText>
            <FormattedMessage
              id="site_product_grid.no_filter_applied"
              defaultMessage="No filters applied"
            />
          </HeaderText>
        </NoFiltersHeaderRow>
      ) : (
        <HeaderRow>
          <HeaderText>
            <FormattedMessage
              id="site_product_filter.filtered_by"
              defaultMessage="Filtered by:"
            />
          </HeaderText>
          <ClearButton
            type="button"
            onClick={handleClearAllFilters}
            aria-label="Clear all filters"
            data-autotag="filter-clear-all"
          >
            <FormattedMessage
              id="site_product_filter.clear_all"
              defaultMessage="Clear all"
            />
          </ClearButton>
        </HeaderRow>
      )}
      <FiltersList data-autotag="filtered-by-textbox">
        {selectedFacets?.map(
          ({ facetName, facetOption, facetOptionDisplayName }) => {
            const isCustomColor = facetName === CUSTOM_COLOR_FILTER.name;
            let colorForDisplay = null;

            if (isCustomColor && facetOption) {
              const [r, g, b] = facetOption
                .split(',')
                .map((v) => parseInt(v, 10));
              colorForDisplay = { r, g, b };
            }

            const renderFilterLabel = () => {
              if (isCustomColor && colorForDisplay) {
                return <ColorWheelFilterItem color={colorForDisplay} />;
              }
              if (isCioSizeFacet(facetName)) {
                return (
                  <>
                    <DynamicFormattedMessage
                      id={`site_product_filter.${getRbidByFacetNameAndFacetOption(
                        facetName,
                        facetOption
                      )}`}
                      defaultMessage={facetOption}
                    />
                    {' ('}
                    <DynamicFormattedMessage
                      id={`site_product_filter.${
                        cioFacetsToUrlFilters[facetName] || facetName
                      }`}
                      defaultMessage={
                        cioFacetsToUrlFilters[facetName] || facetName
                      }
                    />
                    )
                  </>
                );
              }
              return (
                <DynamicFormattedMessage
                  id={`site_product_filter.${getSelectedFacetRbid(
                    facetName,
                    facetOptionDisplayName
                  )}`}
                  defaultMessage={facetOptionDisplayName}
                />
              );
            };

            return (
              <FilterItem
                key={`${facetName}-${facetOption}`}
                data-autotag="filtered-by-option"
              >
                {renderFilterLabel()}
                <RemoveButton
                  type="button"
                  onClick={() =>
                    handleOnClick({
                      facetName,
                      facetOption,
                    })
                  }
                  aria-label={`Remove ${
                    isCustomColor ? 'Custom Color' : facetOptionDisplayName
                  } filter`}
                  data-autotag="filtered-by-option-close-icon"
                >
                  <Icon size={12} />
                </RemoveButton>
              </FilterItem>
            );
          }
        )}
        {Object.values(validCustomFacetFilters).map((customFilter) => (
          <FilterItem key={customFilter.name} data-autotag="filtered-by-option">
            {customFilter.label}
            <RemoveButton
              type="button"
              onClick={() => handleCustomFilterRemove(customFilter)}
              aria-label={`Remove ${customFilter.name} filter`}
              data-autotag="filtered-by-option-close-icon"
            >
              <Icon size={12} />
            </RemoveButton>
          </FilterItem>
        ))}
      </FiltersList>
    </MobileGridSelectedFiltersContainer>
  );
};

MobileGridSelectedFilters.propTypes = {
  closeAllPanels: PropTypes.func.isRequired,
};

export default MobileGridSelectedFilters;
